import { RacemapColors } from '@racemap/utilities/consts/common';
import {
  isPoint,
  validateCoordinates,
  validateFeature,
  validateFeatureCollection,
  validatePoints,
  validatePosition,
} from '@racemap/utilities/functions/validation';
import type { Feature, LineString, Point } from 'geojson';
import type { Immutable } from 'immer';
import type {
  CirclePaint,
  DataDrivenPropertyValueSpecification,
  LineLayerSpecification,
  LinePaint,
  SymbolLayout,
  SymbolPaint,
} from 'mapbox-gl';
import tinycolor from 'tinycolor2';
import type { LabelFeatures, LabelProperties } from './Layers/LabelLayer';
import type { PointLayerPoints, PointProperties } from './Layers/PointLayer';
import type { LineStringProperties, Track } from './Layers/TrackLayer';

export enum AUTOFOCUS {
  EVERYTIME = 'EVERYTIME',
  ONCE = 'ONCE',
  NONE = 'NONE',
}

export function injectFeatureIds<T extends Feature<Point | LineString>>(
  features: Array<T>,
): Array<T> {
  return features.map((f) => ({
    ...f,
    properties: { ...f.properties, _id: f.id },
  }));
}

export function getPointPaint(paint: LinePaint, fillColor?: string): CirclePaint {
  if (paint == null) return {};

  return {
    'circle-radius': paint['line-width'] || 2,
    'circle-color': tinycolor(fillColor || 'white').toHexString(),
    'circle-stroke-color': paint['line-color'] || RacemapColors.PaleBlue,
    'circle-stroke-width': paint['line-width'] || 2,
  };
}

// toHexString() drops the alpha channel, which turns a transparent line black.
function toLineColor(color: string): string {
  const parsed = tinycolor(color);
  return parsed.getAlpha() < 1 ? parsed.toRgbString() : parsed.toHexString();
}

export function convertTracksToFeatures(
  tracks: Array<Track | undefined> | undefined,
  color?: string,
  lineWidth?: number,
): { features: Array<Feature<LineString, LineStringProperties>>; paint: LinePaint } {
  const features: Array<Feature<LineString, LineStringProperties>> = [];
  if (tracks == null) return { features, paint: {} };

  for (const track of tracks) {
    if (track == null) continue;

    if (validatePoints(track)) {
      // track is a bunch of points with lat,lng and time
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: track.map((p) => [p.lng, p.lat]),
        },
        properties: {
          lineWidth,
          color,
        },
      });
    } else if (validateCoordinates(track)) {
      // track are only coordinates
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: track,
        },
        properties: {},
      });
    } else if (validateFeatureCollection(track)) {
      // track is a complete feature collection, take the first feature
      features.push(track.features[0]);
    } else if (validateFeature(track)) {
      // track is a simple feature. no preperation necessary
      features.push(track as Feature<LineString, LineStringProperties>);
    } else {
      throw new Error(
        'The delivered track is not a valid feature, feature collection or coordinates!',
      );
    }
  }

  const lineColor: DataDrivenPropertyValueSpecification<string> =
    color != null
      ? toLineColor(color)
      : ['coalesce', ['get', 'color'], tinycolor(RacemapColors.PaleBlue).toHexString()];
  const paint: LineLayerSpecification['paint'] = {
    'line-width': lineWidth || features[0]?.properties?.lineWidth || 1,
    'line-color': lineColor,
  };

  return { features, paint };
}

export function convertPointsToFeatures(
  points: PointLayerPoints | Immutable<PointLayerPoints>,
  color?: string,
  lineWidth?: number,
  fillColor?: string,
  radius?: DataDrivenPropertyValueSpecification<number>,
  additionalProperties?: Record<string, unknown>,
): { features: Array<Feature<Point, PointProperties>>; paint: CirclePaint } {
  const features: Array<Feature<Point, PointProperties>> = [];
  if (points == null) return { features, paint: {} };

  if (validatePosition(points)) {
    // points are a single coordinate
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: points,
      },
      properties: {
        ...additionalProperties,
      },
    });
  } else if (validateCoordinates(points)) {
    // points are a bunch of coordinates
    points.forEach((p) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: p,
        },
        properties: {
          ...additionalProperties,
        },
      });
    });
  } else if (validatePoints(points)) {
    // points are a bunch of points with lat,lng and time
    points.forEach((p) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [p.lng, p.lat],
        },
        properties: {
          ...additionalProperties,
        },
      });
    });
  } else if (validateFeatureCollection(points)) {
    // track is a complete feature collection, take everything
    features.push(...points.features);
  } else if (Array.isArray(points) && points.every(validateFeature)) {
    // points are a array of point features
    features.push(
      ...points.map(
        (p) =>
          ({
            ...p,
            properties: {
              color: (p.properties as { color?: string }).color || RacemapColors.PaleBlue,
              ...additionalProperties,
            },
          }) as Feature<Point, PointProperties>,
      ),
    );
  } else if (validateFeature(points) && isPoint(points)) {
    // track is a simple feature. no preperation necessary
    features.push({
      ...points,
      properties: {
        color: (points.properties as { color?: string }).color || RacemapColors.PaleBlue,
        ...additionalProperties,
      },
    } as Feature<Point, PointProperties>);
  } else {
    throw new Error(
      'The delivered points are not a valid feature, feature collection or coordinates!',
    );
  }

  const paint: CirclePaint = {
    'circle-radius': radius ?? 2,
    'circle-color': fillColor || 'white',
    'circle-stroke-color': ['coalesce', ['get', 'color'], color || RacemapColors.PaleBlue],
    'circle-stroke-width': lineWidth || 2,
  };

  return { features, paint };
}

export function convertLabelFeaturesToCollection(
  labelFeatures: LabelFeatures | Immutable<LabelFeatures>,
  color?: string,
): { features: Array<Feature<Point, LabelProperties>>; layout: SymbolLayout; paint: SymbolPaint } {
  const features: Array<Feature<Point, LabelProperties>> = [];
  if (labelFeatures == null) return { features, layout: {}, paint: {} };

  if (validatePosition(labelFeatures)) {
    // points are a single coordinate
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: labelFeatures,
      },
      properties: {},
    });
  } else if (validateCoordinates(labelFeatures)) {
    // points are a bunch of coordinates
    labelFeatures.forEach((p) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: p,
        },
        properties: {},
      });
    });
  } else if (validateFeatureCollection(labelFeatures)) {
    // object is a complete feature collection, take everything
    features.push(...labelFeatures.features.filter(isPoint));
  } else if (Array.isArray(labelFeatures) && labelFeatures.every(validateFeature)) {
    // object is a array of point features
    features.push(...labelFeatures.filter(isPoint));
  } else if (validateFeature(labelFeatures) && isPoint(labelFeatures)) {
    // object is a simple feature. no preperation necessary
    features.push(labelFeatures);
  } else {
    throw new Error(
      'The delivered points are not a valid feature, feature collection or coordinates!',
    );
  }

  const layout: SymbolLayout = {
    'text-anchor': 'top',
    'text-offset': [0, 0.2],
    'text-field': [
      'let',
      'name',
      ['get', 'name'],
      'shortName',
      ['get', 'shortName'],
      [
        'case',
        ['all', ['!=', ['var', 'shortName'], ''], ['!=', ['var', 'shortName'], null]],
        ['var', 'shortName'],
        ['var', 'name'],
      ],
    ],
  };
  const paint: SymbolPaint = {
    'text-color': [
      'to-color',
      ['get', 'color'],
      tinycolor(color || RacemapColors.DarkBlue).toHexString(),
    ],
  };

  return { features, layout, paint };
}
